import { useCallback, useState } from "react";

import type { Junction } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { formatDuration } from "../app/format";
import { routes } from "../app/useHashRoute";
import { useFeatureFlag, useNetworkId } from "../config/ConfigProvider";
import { usePost } from "../posts/PostProvider";
import { usePolledResource } from "../cockpit/hooks";
import { LineBadge } from "../cockpit/bits";

/**
 * Vue carrefours (F3.3, CDC §5.1). Recherche par n°/nom, système technique en
 * badge, indicateur « pilotable à distance », lignes impactées, marqueur
 * d'état partagé entre postes (§12.5). Le marqueur se pose depuis cette vue :
 * quatrième et dernière écriture de l'API — une observation partagée, elle
 * n'agit ni sur le réseau ni sur les feux.
 */
export function JunctionsView() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const enabled = useFeatureFlag("junctions");
  const { activePost } = usePost();
  const [query, setQuery] = useState("");
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.listJunctions(networkId, query ? { q: query } : {}, signal),
    [backend, networkId, query, nonce],
    30_000,
  );

  if (!enabled) {
    return <p className="notification is-light is-size-7">Les carrefours ne sont pas activés pour ce réseau.</p>;
  }

  async function setStatus(junctionId: string, state: string | null) {
    if (!activePost) return; // garde côté appelant aussi (bouton déjà désactivé) — post requis (v1.5)
    await backend.api.patchJunctionStatus(networkId, junctionId, { state, post: activePost.id });
    reload();
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 className="ovl">Carrefours</h2>
        <input
          className="input is-small"
          style={{ maxWidth: 260 }}
          type="search"
          placeholder="Numéro ou nom…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && !data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : error ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="has-text-grey is-size-7">Aucun carrefour.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table className="table is-fullwidth is-narrow is-striped" style={{ fontSize: "0.78rem" }}>
          <thead>
            <tr>
              <th className="ovl" style={{ width: 70 }}>N°</th>
              <th className="ovl">Nom</th>
              <th className="ovl">Système</th>
              <th className="ovl">Pilotage</th>
              <th className="ovl">Lignes</th>
              <th className="ovl">État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((junction) => (
              <JunctionRow key={junction.id} junction={junction} canSetStatus={Boolean(activePost)} onSetStatus={setStatus} />
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function JunctionRow({
  junction,
  canSetStatus,
  onSetStatus,
}: {
  junction: Junction;
  canSetStatus: boolean;
  onSetStatus: (junctionId: string, state: string | null) => void;
}) {
  return (
    <tr>
      <td className="mono">{junction.code}</td>
      <td>{junction.name}</td>
      <td>
        {junction.techSystem ? (
          <span className="tag is-small mono" style={{ background: systemColor(junction.techSystem), color: "#fff" }}>
            {junction.techSystem}
          </span>
        ) : (
          <span className="has-text-grey">—</span>
        )}
      </td>
      <td>
        <span className="ovl" style={{ color: junction.remoteControllable ? "var(--sentinelle-ok)" : "var(--sentinelle-ink-3)" }}>
          ● {junction.remoteControllable ? "à distance" : "local seul"}
        </span>
      </td>
      <td>
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {junction.impactedLineIds.map((id) => (
            <LineBadge key={id} lineId={id} size={20} />
          ))}
          {junction.impactedLineIds.length === 0 ? <span className="has-text-grey">—</span> : null}
        </span>
      </td>
      <td>
        {junction.status.state ? (
          <span className="ovl" style={{ color: "var(--sentinelle-signal)" }} title={junction.status.note ?? undefined}>
            {junction.status.state}
            {junction.status.unresolvedForSeconds !== null
              ? ` · ${formatDuration(junction.status.unresolvedForSeconds)}`
              : ""}
          </span>
        ) : (
          <span className="has-text-grey">—</span>
        )}
      </td>
      <td className="has-text-right" style={{ whiteSpace: "nowrap" }}>
        {junction.status.state ? (
          <button
            type="button"
            className="button is-small is-white"
            disabled={!canSetStatus}
            title={canSetStatus ? undefined : "Sélectionner un poste pour lever ce marqueur"}
            onClick={() => onSetStatus(junction.id, null)}
          >
            Lever
          </button>
        ) : (
          <button
            type="button"
            className="button is-small is-white"
            disabled={!canSetStatus}
            onClick={() => onSetStatus(junction.id, "flashing")}
            title={canSetStatus ? "Poser un marqueur d'observation — n'agit pas sur les feux" : "Sélectionner un poste pour signaler"}
          >
            Signaler
          </button>
        )}
        <a className="button is-small is-white" href={routes.map} title="Voir sur la carte pilote" style={{ marginLeft: 4 }}>
          Carte
        </a>
      </td>
    </tr>
  );
}

/** Couleur stable par nom de système technique (badge distinct, CDC §5.1). */
function systemColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  const palette = ["#2b6cb0", "#00794f", "#6d3b8a", "#b45309", "#8a1c1c", "#334155"];
  return palette[hash % palette.length]!;
}
